import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Empty,
  Input,
  Select,
  Space,
  Table,
  type TableColumnProps,
} from '@arco-design/web-react';
import { IconPlus, IconSearch } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';
import { PageHeader, StatusPill } from '@/components/ui';
import { billingApi, cardsApi } from '@/api';
import type { CardLifecycleStatus, VccCard } from '@/types/card';
import { CardStatusMeta } from '@/types/card';
import { formatMoney } from '@/utils/money';
import { useAuthStore } from '@/store/authStore';
import ApplyCardModal from './ApplyCardModal';

const statusOptions: Array<{ label: string; value: CardLifecycleStatus | 'ALL' }> = [
  { label: '全部状态', value: 'ALL' },
  ...(Object.keys(CardStatusMeta) as CardLifecycleStatus[]).map((key) => ({
    value: key,
    label: CardStatusMeta[key].label,
  })),
];

export default function CardWalletView() {
  const navigate = useNavigate();
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  const [list, setList] = useState<VccCard[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<CardLifecycleStatus | 'ALL'>('ALL');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [cardholderNames, setCardholderNames] = useState<Record<string, string>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [result, billingResult] = await Promise.all([
        cardsApi.list({
          page,
          pageSize,
          status: status === 'ALL' ? undefined : status,
          keyword: keyword || undefined,
        }),
        billingApi.list({ page: 1, pageSize: 100 }),
      ]);
      setList(result.items);
      setTotal(result.total);
      setCardholderNames(
        Object.fromEntries(
          billingResult.items.map((profile) => [
            profile.id,
            `${profile.firstName} ${profile.lastName}`.trim(),
          ]),
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, keyword]);

  useEffect(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);

  const columns: TableColumnProps<VccCard>[] = [
    {
      title: '单次/常驻',
      dataIndex: 'reloadable',
      width: 100,
      render: (v: boolean) => (v ? '常驻卡' : '单次卡'),
    },
    {
      title: '卡类型',
      dataIndex: 'brand',
      width: 120,
    },
    {
      title: '卡号',
      dataIndex: 'maskedPan',
      width: 180,
      render: (v: string) => (
        <span style={{ fontFamily: 'SFMono-Regular, Consolas, monospace' }}>{v || '—'}</span>
      ),
    },
    {
      title: '卡片别名',
      dataIndex: 'alias',
      ellipsis: true,
      render: (v: string) => v || <span style={{ color: '#86909C' }}>—</span>,
    },
    {
      title: '持卡人',
      dataIndex: 'billingProfileId',
      width: 140,
      ellipsis: true,
      render: (v: string) => cardholderNames[v] || '—',
    },
    {
      title: '余额',
      dataIndex: 'availableBalanceCents',
      width: 120,
      align: 'right',
      render: (v: number) => (
        <span style={{ fontFamily: 'SFMono-Regular, Consolas, monospace' }}>{formatMoney(v)}</span>
      ),
    },
    {
      title: '最后使用日期',
      dataIndex: 'lastUsedAt',
      width: 160,
      render: (v?: string | null) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm') : <span style={{ color: '#86909C' }}>—</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (v: CardLifecycleStatus) => {
        const meta = CardStatusMeta[v];
        return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
      },
    },
  ];

  return (
    <div className="up-page">
      <PageHeader
        title="我的卡包"
        subtitle="以列表查看所持卡片，点击行进入详情与消费明细"
        extra={
          <Button type="primary" icon={<IconPlus />} onClick={() => setApplyOpen(true)}>
            申请新卡
          </Button>
        }
      />

      <div className="up-section-card" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={status}
            style={{ width: 160 }}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          >
            {statusOptions.map((o) => (
              <Select.Option key={o.value} value={o.value}>
                {o.label}
              </Select.Option>
            ))}
          </Select>
          <Input.Search
            allowClear
            placeholder="搜索别名 / 脱敏卡号"
            prefix={<IconSearch />}
            style={{ width: 280 }}
            onSearch={(v) => {
              setKeyword(v);
              setPage(1);
            }}
          />
        </Space>
      </div>

      <div className="up-section-card">
        <Table
          rowKey="id"
          loading={loading}
          data={list}
          columns={columns}
          scroll={{ x: 1100 }}
          noDataElement={<Empty description="暂无卡片，点击右上角申请新卡" />}
          onRow={(record) => ({
            onClick: () => navigate(`/cards/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            total,
            current: page,
            pageSize,
            sizeCanChange: true,
            showTotal: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </div>

      <ApplyCardModal
        visible={applyOpen}
        onCancel={() => setApplyOpen(false)}
        onSuccess={() => {
          setApplyOpen(false);
          fetchData();
          void refreshProfile();
        }}
      />
    </div>
  );
}
